<template>
  <el-dialog
    v-model="_isShow"
    width="90%"
    top="10vh"
    lock-scroll
    custom-class="rfid-device-selector-dialog"
    center
    show-close
    destroy-on-close
    :title="$t('Select RFID Device')"
  >
    <div class="device-list">
      <div
        v-for="deviceKey in sortedDeviceKeys"
        :key="deviceKey"
        class="device-item"
        :class="{ 'device-item-selected': selectedDeviceKey === deviceKey }"
        @click="selectDevice(deviceKey)"
      >
        <div class="device-name">{{ $t(`rfid_device.${deviceKey}`) }}</div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="_isShow = false">
          {{ $t('cancel') }}
        </el-button>
        <el-button type="primary" @click="onConfirm" :disabled="!selectedDeviceKey">
          {{ $t('connect') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { PropSync, Emit } from 'vue-property-decorator'
import rfidDevicesConfig from './config/rfidDevices.json'
import { IScannerSettings } from '@/store/bizBrowser/interface'
import { UPDATE_BIZ_BROWSER_STATE } from '@/store/actions'
import { LOCAL_STORAGE_ITEM } from '@/utils/constants'

@Options({
  components: {},
  emits: ['update:isShow', 'confirm'],
  name: 'RfidDeviceSelector',
})
export default class RfidDeviceSelector extends Vue {
  @PropSync('isShow', { type: Boolean }) _isShow!: boolean

  devices: Record<string, IScannerSettings> = rfidDevicesConfig
  selectedDeviceKey = ''

  mounted() {
    // Set initial selection to current device in store
    const currentDeviceKey = this.$store.getters.bizBrowserState.selectedDeviceKey
    if (currentDeviceKey) {
      this.selectedDeviceKey = currentDeviceKey
    }
  }

  get sortedDeviceKeys(): string[] {
    const deviceKeys = Object.keys(this.devices)
    const history = this.getDeviceHistory()

    const sortedKeys = [...deviceKeys].sort((a, b) => {
      const indexA = history.indexOf(a)
      const indexB = history.indexOf(b)
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB
      }
      if (indexA !== -1) {
        return -1
      }
      if (indexB !== -1) {
        return 1
      }
      return 0
    })

    return sortedKeys
  }

  getDeviceHistory(): string[] {
    try {
      const history = localStorage.getItem(LOCAL_STORAGE_ITEM.RFID_DEVICE_HISTORY)
      return history ? JSON.parse(history) : []
    } catch (error) {
      console.error('Failed to load device history:', error)
      return []
    }
  }

  saveDeviceHistory(deviceKey: string) {
    try {
      const history = this.getDeviceHistory()

      const filteredHistory = history.filter((key) => key !== deviceKey)

      const newHistory = [deviceKey, ...filteredHistory]

      localStorage.setItem(LOCAL_STORAGE_ITEM.RFID_DEVICE_HISTORY, JSON.stringify(newHistory))
    } catch (error) {
      console.error('Failed to save device history:', error)
    }
  }

  selectDevice(key: string) {
    this.selectedDeviceKey = key
  }

  @Emit('confirm')
  emitConfirm() {
    return {
      key: this.selectedDeviceKey,
      device: this.devices[this.selectedDeviceKey],
    }
  }

  onConfirm() {
    this.saveDeviceHistory(this.selectedDeviceKey)

    this.$store.dispatch(UPDATE_BIZ_BROWSER_STATE, {
      selectedDeviceKey: this.selectedDeviceKey,
      scannerSettings: this.devices[this.selectedDeviceKey],
    })

    this.emitConfirm()
    this._isShow = false
  }
}
</script>

<style lang="scss" scoped>
.device-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 60vh;
  overflow-y: auto;
}

.device-item {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  user-select: none;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #409eff;
  }

  &.device-item-selected {
    background-color: #ecf5ff;
    border-color: #409eff;
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.2);
  }
}

.device-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #303133;
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
</style>

<style lang="scss">
.rfid-device-selector-dialog.el-dialog--center .el-dialog__body {
  padding: 1rem 1.25rem 0.5rem 1.25rem !important;
}
</style>
